import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import {
  Lock, CheckCircle2, Play, Calendar, Trophy, Star,
  BookOpen, Library, Users, FileText, PenTool, Gamepad2,
  Sparkles, Target, Clock, ChevronRight, LogOut, Bell,
  Key, Shield, AlertCircle, Unlock, Loader2, ArrowLeft
} from 'lucide-react';
import EdpiesLogo from '@/components/EdpiesLogo';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Week status types
const WEEK_STATUS = {
  LOCKED: 'locked',
  AVAILABLE: 'available',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed'
};

// Helper to get subjects based on year group
const getSubjectsForYearGroup = (yearGroup = '') => {
  const normalized = yearGroup.toLowerCase();
  if (['year-5', 'year-6', '11-plus', '7-plus', '11+', '7+'].some(s => normalized.includes(s))) {
    return ['maths', 'english', 'verbal_reasoning', 'non_verbal_reasoning'];
  } else if (['year-7', 'year-8', 'year-9', 'year-10', 'year-11'].some(s => normalized.includes(s))) {
    return ['maths', 'english', 'science'];
  }
  return ['maths', 'english'];
};

// Generate 16 weeks of learning content based on diagnostic results
const generateWeeksContent = (diagnosticResults, userYearGroup = '') => {
  const weakAreas = [];
  const strongAreas = [];
  
  Object.entries(diagnosticResults || {}).forEach(([subject, result]) => {
    if (result.overall_score < 60) {
      weakAreas.push({ subject, score: result.overall_score, level: result.mastery_level });
    } else if (result.overall_score >= 80) {
      strongAreas.push({ subject, score: result.overall_score, level: result.mastery_level });
    } else {
      weakAreas.push({ subject, score: result.overall_score, level: result.mastery_level });
    }
  });

  // Sort weak areas by score (lowest first for priority)
  weakAreas.sort((a, b) => a.score - b.score);

  const weeks = [];
  const subjects = getSubjectsForYearGroup(userYearGroup);
  
  for (let i = 0; i < 16; i++) {
    // Rotate through subjects with emphasis on weak areas
    const primarySubject = weakAreas.length > 0 
      ? weakAreas[i % weakAreas.length].subject 
      : subjects[i % subjects.length];
    
    const secondarySubject = subjects[(i + 1) % subjects.length];
    
    weeks.push({
      weekNumber: i + 1,
      title: `Week ${i + 1}`,
      focus: primarySubject,
      secondaryFocus: secondarySubject,
      status: i === 0 ? WEEK_STATUS.AVAILABLE : WEEK_STATUS.LOCKED,
      progress: 0,
      modules: [
        { day: 'Monday', type: 'learn', subject: primarySubject, title: 'Core Concepts', completed: false },
        { day: 'Tuesday', type: 'practice', subject: primarySubject, title: 'Guided Practice', completed: false },
        { day: 'Wednesday', type: 'learn', subject: secondarySubject, title: 'Skills Building', completed: false },
        { day: 'Thursday', type: 'challenge', subject: primarySubject, title: 'Problem Solving', completed: false },
        { day: 'Friday', type: 'mock', subject: 'all', title: 'Weekly Assessment', completed: false, locked: true }
      ],
      mockUnlocked: false
    });
  }
  
  return weeks;
};

// Subject colors and icons
const SUBJECT_CONFIG = {
  maths: { color: 'from-blue-600 to-indigo-600', bg: 'bg-blue-500', text: 'text-blue-600', name: 'Maths' },
  english: { color: 'from-green-600 to-emerald-600', bg: 'bg-green-500', text: 'text-green-600', name: 'English' },
  verbal_reasoning: { color: 'from-purple-600 to-pink-600', bg: 'bg-purple-500', text: 'text-purple-600', name: 'VR' },
  non_verbal_reasoning: { color: 'from-orange-600 to-red-600', bg: 'bg-orange-500', text: 'text-orange-600', name: 'NVR' },
  all: { color: 'from-amber-600 to-orange-600', bg: 'bg-amber-500', text: 'text-amber-600', name: 'Mixed' }
};

const LearningPathGrid = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const diagnosticResults = progress?.diagnosticResults || {};
  const userName = progress?.user?.name?.split(' ')[0] || 'Learner';
  const userYearGroup = progress?.user?.year_group || progress?.user?.yearGroup || '';
  
  // Determine if user should see VR/NVR based on year group
  const isSelectiveStage = ['year-5', 'year-6', '11-plus', '7-plus', '11+', '7+'].some(
    stage => userYearGroup.toLowerCase().includes(stage.toLowerCase())
  );

  // Map the user's year-group / exam goal to the canonical stage slug used by
  // the backend question banks. Without this map, deep-links to "Practice
  // Library", "Mock Exams" and "Challenger Mode" all hardcoded `11plus` and
  // returned 11+ questions to 7+ and GCSE students.
  const userStageSlug = (() => {
    const yg = userYearGroup.toLowerCase();
    const examGoal = (progress?.user?.studentProfile?.examGoal || '').toLowerCase();
    // Stricter regex matching avoids "year 10" being caught by a "year 1" substring check.
    if (/year[\s-]?1[01]\b/.test(yg) || examGoal === 'gcse') return 'gcse';
    if (/year[\s-]?[789]\b/.test(yg)) return 'year-9';
    if (/year[\s-]?[12]\b/.test(yg) || examGoal === '7+' || examGoal === '7plus') return '7plus';
    return '11plus';
  })();
  // Default subject for shortcuts that need one (Maths is universal).
  const userPrimarySubject = 'maths';
  
  // Get appropriate subjects based on year group
  const getSubjectsForUser = () => {
    if (isSelectiveStage) {
      return ['maths', 'english', 'verbal_reasoning', 'non_verbal_reasoning'];
    } else if (userYearGroup.includes('year-7') || userYearGroup.includes('year-8') || userYearGroup.includes('year-9') ||
               userYearGroup.includes('year-10') || userYearGroup.includes('year-11')) {
      return ['maths', 'english', 'science'];
    } else {
      return ['maths', 'english'];
    }
  };
  
  // State
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [unlockWeekNumber, setUnlockWeekNumber] = useState(null);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [stats, setStats] = useState(null);

  // Fetch learning path progress from backend
  useEffect(() => {
    fetchLearningPath();
    fetchStats();
  }, []);

  const fetchLearningPath = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/learning-path/progress`, { headers });
      
      // Transform backend data to match frontend structure
      const backendWeeks = response.data.weeks || [];
      const transformedWeeks = backendWeeks.map(w => ({
        weekNumber: w.week_number,
        title: `Week ${w.week_number}`,
        focus: w.focus_subject || 'maths',
        secondaryFocus: getSecondaryFocus(w.focus_subject),
        status: w.status,
        progress: w.progress,
        modules: generateModulesForWeek(w),
        mockUnlocked: w.mock_unlocked
      }));
      
      setWeeks(transformedWeeks);
    } catch (err) {
      console.error('Failed to fetch learning path:', err);
      // Fall back to generated weeks if API fails
      setWeeks(generateWeeksContent(diagnosticResults));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/learning-path/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const getSecondaryFocus = (primary) => {
    const subjects = getSubjectsForUser();
    const idx = subjects.indexOf(primary);
    return subjects[(idx + 1) % subjects.length];
  };

  const generateModulesForWeek = (weekData) => {
    const focus = weekData.focus_subject || 'maths';
    const secondary = getSecondaryFocus(focus);
    const completed = weekData.modules_completed || [];
    
    return [
      { day: 'Monday', type: 'learn', subject: focus, title: 'Core Concepts', completed: completed.includes(0) },
      { day: 'Tuesday', type: 'practice', subject: focus, title: 'Guided Practice', completed: completed.includes(1) },
      { day: 'Wednesday', type: 'learn', subject: secondary, title: 'Skills Building', completed: completed.includes(2) },
      { day: 'Thursday', type: 'challenge', subject: focus, title: 'Problem Solving', completed: completed.includes(3) },
      { day: 'Friday', type: 'mock', subject: 'all', title: 'Weekly Assessment', completed: completed.includes(4), locked: !weekData.mock_unlocked }
    ];
  };

  // Calculate overall progress
  const completedWeeks = weeks.filter(w => w.status === WEEK_STATUS.COMPLETED).length;
  const overallProgress = stats?.overall_progress || (completedWeeks / 16) * 100;

  // Handle week click
  const handleWeekClick = (week) => {
    if (week.status === WEEK_STATUS.LOCKED) {
      setUnlockWeekNumber(week.weekNumber);
      setShowUnlockDialog(true);
      return;
    }
    
    // Navigate to week detail view
    navigate(`/learning-path/week/${week.weekNumber}`);
  };

  // Handle unlock with code - now uses backend API
  const handleUnlock = async () => {
    setUnlocking(true);
    setUnlockError('');
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/learning-path/week/unlock`, {
        week_number: unlockWeekNumber,
        unlock_code: unlockCode
      }, { headers });
      
      // Update local state
      setWeeks(prev => prev.map(w => 
        w.weekNumber === unlockWeekNumber 
          ? { ...w, status: WEEK_STATUS.AVAILABLE }
          : w
      ));
      setShowUnlockDialog(false);
      setUnlockCode('');
      toast.success(`Week ${unlockWeekNumber} unlocked!`);
      
      // Refresh stats
      fetchStats();
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid unlock code. Please contact your parent or admin.';
      setUnlockError(message);
    } finally {
      setUnlocking(false);
    }
  };

  // Get status badge config
  const getStatusConfig = (status) => {
    switch (status) {
      case WEEK_STATUS.COMPLETED:
        return { icon: CheckCircle2, color: 'bg-green-500', text: 'Completed' };
      case WEEK_STATUS.IN_PROGRESS:
        return { icon: Play, color: 'bg-blue-500', text: 'In Progress' };
      case WEEK_STATUS.AVAILABLE:
        return { icon: Sparkles, color: 'bg-violet-500', text: 'Available' };
      default:
        return { icon: Lock, color: 'bg-slate-200', text: 'Locked' };
    }
  };

  const handleLogout = () => {
    updateProgress(null);
    navigate('/');
  };

  // Additional resources
  const resources = [
    { id: 'library', title: 'Ed Library', description: 'Reading & vocabulary', icon: Library, path: '/elibrary', color: 'from-green-600 to-emerald-600' },
    { id: 'challenger', title: 'Challenger Mode', description: 'Unlimited practice', icon: Gamepad2, path: `/challenger/${userStageSlug}/${userPrimarySubject}`, color: 'from-purple-600 to-pink-600' },
    { id: 'whiteboard', title: 'Whiteboard X', description: 'Weekly challenges', icon: PenTool, path: '/whiteboard-x', color: 'from-orange-600 to-red-600' },
    { id: 'mock', title: 'Mock Exams', description: 'Full-length tests', icon: FileText, path: '/adaptive-mock', color: 'from-amber-600 to-orange-600' },
    { id: 'practice', title: 'Practice Library', description: 'All practice papers', icon: BookOpen, path: `/papers/${userStageSlug}/${userPrimarySubject}`, color: 'from-blue-600 to-indigo-600' }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading your learning path...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="learning-path-grid"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white"
    >
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Back Button */}
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)} 
                className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 mr-2"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <EdpiesLogo size="md" />
              <div>
                <h1 className="text-lg font-bold text-slate-900">My Learning Path</h1>
                <p className="text-xs text-slate-500">16-Week Adaptive Programme</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Trophy className="w-4 h-4 text-amber-600" />
                <span className="text-amber-600 font-medium">{completedWeeks}/16 weeks</span>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-900">
                <Bell className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-500 hover:text-slate-900">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Welcome back, <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">{userName}</span>!
              </h2>
              <p className="text-slate-500">
                Your personalised 16-week learning journey. Complete each week to unlock the next.
              </p>
            </div>
            
            <Card className="bg-white border-slate-200 w-full md:w-auto">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-violet-500/20">
                    <Target className="w-6 h-6 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500">Overall Progress</p>
                    <p className="text-xl font-bold text-slate-900">{Math.round(overallProgress)}%</p>
                  </div>
                  <div className="w-24">
                    <Progress value={overallProgress} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* 16-Week Calendar Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-violet-600" />
            <h3 className="text-lg font-semibold text-slate-900">Learning Calendar</h3>
            <Badge className="bg-violet-500/20 text-violet-600 ml-2">
              <Sparkles className="w-3 h-3 mr-1" />
              Personalised
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {weeks.map((week, index) => {
              const statusConfig = getStatusConfig(week.status);
              const StatusIcon = statusConfig.icon;
              const subjectConfig = SUBJECT_CONFIG[week.focus] || SUBJECT_CONFIG.maths;
              const isLocked = week.status === WEEK_STATUS.LOCKED;
              const isCompleted = week.status === WEEK_STATUS.COMPLETED;
              const isAvailable = week.status === WEEK_STATUS.AVAILABLE;
              const isInProgress = week.status === WEEK_STATUS.IN_PROGRESS;

              return (
                <motion.div
                  key={week.weekNumber}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Card
                    data-testid={`week-tile-${week.weekNumber}`}
                    className={`relative overflow-hidden cursor-pointer transition-all duration-300 ${
                      isLocked 
                        ? 'bg-white/50 border-slate-200 opacity-60 hover:opacity-80' 
                        : isCompleted
                          ? 'bg-green-900/20 border-green-500/30 hover:border-green-400/50'
                          : isInProgress
                            ? 'bg-blue-900/20 border-blue-500/30 hover:border-blue-400/50'
                            : 'bg-white/50 border-violet-500/30 hover:border-violet-400/50'
                    }`}
                    onClick={() => handleWeekClick(week)}
                  >
                    {/* Subject color stripe */}
                    <div className={`h-1.5 bg-gradient-to-r ${subjectConfig.color}`} />
                    
                    <CardContent className="p-4">
                      {/* Week Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-lg font-bold text-slate-900">Week {week.weekNumber}</p>
                          <p className="text-xs text-slate-500">{subjectConfig.name} Focus</p>
                        </div>
                        <div className={`p-1.5 rounded-lg ${isLocked ? 'bg-slate-100' : statusConfig.color + '/20'}`}>
                          <StatusIcon className={`w-4 h-4 ${isLocked ? 'text-slate-500' : isCompleted ? 'text-green-600' : 'text-violet-600'}`} />
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-500">Progress</span>
                          <span className={isLocked ? 'text-slate-500' : 'text-slate-900'}>{week.progress}%</span>
                        </div>
                        <Progress 
                          value={week.progress} 
                          className={`h-1.5 ${isLocked ? 'opacity-50' : ''}`}
                        />
                      </div>

                      {/* Status Badge */}
                      <div className="flex items-center justify-between">
                        <Badge 
                          className={`text-xs ${
                            isLocked ? 'bg-slate-100 text-slate-500' :
                            isCompleted ? 'bg-green-500/20 text-green-600' :
                            isInProgress ? 'bg-blue-500/20 text-blue-600' :
                            'bg-violet-500/20 text-violet-600'
                          }`}
                        >
                          {statusConfig.text}
                        </Badge>
                        
                        {!isLocked && (
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        )}
                        
                        {isLocked && (
                          <Key className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Additional Resources Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <BookOpen className="w-5 h-5 text-violet-600" />
            <h3 className="text-lg font-semibold text-slate-900">Additional Resources</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {resources.map((resource) => (
              <Card
                key={resource.id}
                data-testid={`resource-${resource.id}`}
                className="bg-white/50 border-slate-200 hover:border-slate-200 transition-all cursor-pointer group"
                onClick={() => navigate(resource.path)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${resource.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <resource.icon className="w-6 h-6 text-slate-900" />
                  </div>
                  <p className="font-medium text-slate-900 text-sm mb-1">{resource.title}</p>
                  <p className="text-xs text-slate-500">{resource.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Unlock Dialog */}
      <Dialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Unlock Week {unlockWeekNumber}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              This week is locked because the previous week hasn't been completed yet. 
              If you have an unlock code from your parent or admin, enter it below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-600">Unlock Code</label>
              <Input
                type="text"
                placeholder="Enter unlock code..."
                value={unlockCode}
                onChange={(e) => {
                  setUnlockCode(e.target.value);
                  setUnlockError('');
                }}
                className="bg-slate-100 border-slate-200 text-slate-900"
              />
              {unlockError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {unlockError}
                </p>
              )}
            </div>
            
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-600">
                <strong>Tip:</strong> Complete Week {unlockWeekNumber - 1} to automatically unlock this week.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUnlockDialog(false)} disabled={unlocking}>
              Cancel
            </Button>
            <Button 
              onClick={handleUnlock}
              disabled={unlocking || !unlockCode.trim()}
              className="bg-gradient-to-r from-amber-600 to-orange-600"
            >
              {unlocking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Unlock className="w-4 h-4 mr-2" />
              )}
              {unlocking ? 'Unlocking...' : 'Unlock Week'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LearningPathGrid;
